import type { ScanResult } from "@vocifuchs/shared";
import { api, ApiError, OFFLINE } from "./api.js";
import { errors } from "../copy/errors.js";

/** docs/ARCHITECTURE.md §7: the phone does the heavy lifting, not the worker. */
const MAX_EDGE = 1600;
const QUALITY = 0.72;
/** A real scan takes 20 s or more; a drop before this is not about speed. */
const SLOW_DROP_MS = 5_000;

/**
 * Scale the photo down and re-encode it as JPEG. Drawing through a canvas
 * also drops every EXIF field -- location, device, time -- before a single
 * byte leaves the phone. `imageOrientation` keeps a portrait photo upright.
 */
export async function shrinkPhoto(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("no canvas");
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", QUALITY));
  if (!blob) throw new Error("encode failed");
  return blob;
}

export async function scanPhoto(file: File, langB: "en" | "fr"): Promise<ScanResult> {
  const form = new FormData();
  form.set("image", await shrinkPhoto(file), "scan.jpg");
  form.set("lang_b", langB);
  const started = Date.now();
  try {
    return await api<ScanResult>("/api/scan", { method: "POST", body: form });
  } catch (err) {
    // No answer at all when the connection drops. After a long wait that is
    // the phone or the network giving up on a slow scan (#82); straight away
    // it is just being offline, and says so.
    if (err instanceof ApiError && err.code === OFFLINE && Date.now() - started >= SLOW_DROP_MS) {
      throw new ApiError(0, "scan_dropped", errors.failed);
    }
    throw err;
  }
}
